import type { ReactNode } from 'react';

/**
 * A verdict in two words — "Nothing wrong", "Protected", "1 problem".
 *
 * The redesign's summary-before-detail rule (docs/console-redesign-plan.md §1,
 * principle 3) starts here: a card leads with one of these, then the sentence,
 * then the table. The tone is the meaning, and there are only five:
 *
 *  * `ok` — nothing on our side says otherwise (never "healthy": the console
 *    cannot know that, only that nothing is failing);
 *  * `warn` — somebody should look;
 *  * `danger` — something is failing now;
 *  * `accent` — a fact worth seeing that is neither good nor bad;
 *  * `plain` — a label, with no dot, for a property rather than a state.
 *
 * No tone is the neutral grey with a dot: a state that is none of the above
 * ("Not connected", "Not published").
 *
 * This is not `StatusPill`. A pill prints a **wire token** (`live`,
 * `pending_dns`) verbatim; a chip prints **words**. Phase B moves screens from
 * one to the other as their copy is rewritten.
 */
export type ChipTone = 'ok' | 'warn' | 'danger' | 'accent' | 'plain';

export function Chip({
  tone,
  title,
  children,
}: {
  tone?: ChipTone | undefined;
  /** The longer sentence, on hover — the chip stays two words. */
  title?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <span className={tone === undefined ? 'chip' : `chip chip--${tone}`} title={title}>
      {children}
    </span>
  );
}
